import { type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Icon from './Icon.tsx'
import { t } from 'i18next'

type ModalDialogProps = {
  open: boolean
  title: string | undefined
  children: ReactNode
  actions?: ReactNode
  className?: string
} & (
  | {
      showCloseIcon?: true
      onClose: () => void
    }
  | {
      showCloseIcon?: false
      onClose?: never
    }
)

export function ModalDialog({
  open,
  title,
  showCloseIcon = false,
  children,
  actions,
  onClose,
  className = '',
}: ModalDialogProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose()
    }

    if (open) {
      document.addEventListener('keydown', handleKey)
    }

    return () => {
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, onClose])

  if (!open) return null

  const preventContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return createPortal(
    <div
      className="animate-fade-in fixed inset-0 z-[100] flex items-center justify-center bg-black/85"
      onContextMenu={preventContextMenu}
    >
      <div
        className={`animate-scale-in w-full rounded-2xl text-white shadow-xl ${className}`}
        onContextMenu={preventContextMenu}
      >
        <div className="relative flex w-full items-center justify-center bg-zinc-700 font-semibold">
          {title && <h2 className="p-2 font-semibold">{t(title)}</h2>}
          {showCloseIcon && (
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
            >
              <Icon name="xMark" />
            </button>
          )}
        </div>
        <div className="bg-neutral-900 p-4">
          <div>{children}</div>
          {actions && (
            <div className="mt-6 flex justify-end gap-4">{actions}</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
